import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaTimes, FaListUl, FaCrown, FaPen, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { updateCombat, deleteCombat } from "../../../services/combatsServices";
import { getCompetiteurById } from "../../../services/competiteursServices";

/* =========================
   Barème points (Shido = 0)
   (ajuste si besoin)
========================= */
const WEIGHTS = {
  ippon: 10,
  wazari: 7,
  yuko: 5,
  shido: 0, // ✅ pénalité => 0 pt (selon ta demande)
};

const safeInt = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.trunc(n));
};

const computePoints = (s) =>
  safeInt(s.ippon) * WEIGHTS.ippon +
  safeInt(s.wazari) * WEIGHTS.wazari +
  safeInt(s.yuko) * WEIGHTS.yuko +
  safeInt(s.shido) * WEIGHTS.shido;

/* =========================
   Construction details_combat auto
========================= */
const buildSideLine = (name, scores) => {
  const pts = computePoints(scores);
  return `${name} — ${safeInt(scores.ippon)} Ippon — ${safeInt(
    scores.wazari
  )} Waza-ari — ${safeInt(scores.yuko)} Yuko — ${safeInt(
    scores.shido
  )} Shido (${pts} pts)`;
};

// issue: V1, V2, N, H1, H2, F1, F2
const winnerFromIssue = (issue) => {
  if (issue === "V1") return 1;
  if (issue === "V2") return 2;
  if (issue === "H1") return 2; // H1 = comp1 Hansoku => comp2 gagne
  if (issue === "H2") return 1;
  if (issue === "F1") return 2; // F1 = comp1 forfait => comp2 gagne
  if (issue === "F2") return 1;
  return null; // N
};

const decisionLabelFromIssue = (issue, decision) => {
  if (issue === "H1" || issue === "H2") return "Hansoku-make";
  if (issue === "F1" || issue === "F2") return "Forfait";
  if (issue === "N") return "Égalité";
  return decision || "Décision";
};

const buildDetailsCombat = ({ issue, decision, name1, name2, s1, s2 }) => {
  const sideA = buildSideLine(name1, s1);
  const sideB = buildSideLine(name2, s2);

  if (issue === "N") {
    return `Égalité • ${sideA} / ${sideB}`;
  }

  const winner = winnerFromIssue(issue);
  const winnerName = winner === 1 ? name1 : name2;
  const decisionLabel = decisionLabelFromIssue(issue, decision);

  return `Gagnant : ${winnerName} • ${decisionLabel} | ${sideA} / ${sideB}`;
};

/* --------- Affichage : met en valeur le gagnant --------- */
const renderDetails = (raw) => {
  const text = String(raw || "").trim();
  if (!text) return <span>—</span>;

  if (text.startsWith("Égalité")) {
    const [header, rest = ""] = text.split("•");
    const [sideA = "", sideB = ""] = rest.split(" / ");
    return (
      <div className="space-y-1.5">
        <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-500/10 text-amber-700 dark:text-amber-300 px-2.5 py-1 text-xs font-semibold">
          {header.trim()}
        </div>
        <div className="leading-relaxed">
          <div>{sideA?.trim()}</div>
          <div>{sideB?.trim()}</div>
        </div>
      </div>
    );
  }

  const [left, tail = ""] = text.split("|");
  const [winnerChunk = "", decision = ""] = (left || "")
    .replace(/^Gagnant\s*:\s*/i, "")
    .split("•");
  const winner = winnerChunk.trim();
  const decisionLabel = decision.trim();
  const [sideA = "", sideB = ""] = tail.split(" / ");

  const emphasizeSide = (s) => {
    const t = (s || "").trim();
    if (!t) return null;

    if (winner && t.startsWith(winner)) {
      const rest = t.slice(winner.length);
      return (
        <span className="font-semibold text-emerald-700 dark:text-emerald-300">
          <span className="underline decoration-emerald-400/60 underline-offset-2">
            {winner}
          </span>
          <span className="font-normal text-slate-800 dark:text-slate-200">
            {rest}
          </span>
        </span>
      );
    }

    const [maybeName, ...tail2] = t.split(" — ");
    return (
      <span>
        <span className="font-semibold text-slate-900 dark:text-slate-100">
          {maybeName}
        </span>
        {tail2.length > 0 && (
          <span className="text-slate-800 dark:text-slate-200">
            {" "}
            — {tail2.join(" — ")}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="space-y-1.5">
      <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2.5 py-1 text-xs font-semibold">
        <FaCrown className="opacity-80" />
        <span>Gagnant&nbsp;:</span>
        <span className="font-extrabold tracking-tight">{winner}</span>
        {decisionLabel && (
          <span className="ml-2 pl-2 border-l border-emerald-400/30 text-emerald-600 dark:text-emerald-300">
            {decisionLabel}
          </span>
        )}
      </div>

      <div className="leading-relaxed space-y-1">
        <div>{emphasizeSide(sideA)}</div>
        <div>{emphasizeSide(sideB)}</div>
      </div>
    </div>
  );
};

const CombatDetailsModal = ({ isOpen, onClose, combats = [], onChanged }) => {
  /* =========================
     Local combats (édition)
  ========================= */
  const [localCombats, setLocalCombats] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  /* =========================
     Cache compétiteurs par id
  ========================= */
  const [byId, setById] = useState({}); // { [id]: { nom, prenom } }
  const inFlight = useRef(new Set());

  const getName = (cid) => {
    const c = byId?.[cid];
    if (c?.nom || c?.prenom) return `${c?.prenom || ""} ${c?.nom || ""}`.trim();
    return cid ? `#${cid}` : "—";
  };

  const fetchCompetiteur = async (cid) => {
    if (!cid) return;
    if (byId?.[cid]) return;
    if (inFlight.current.has(cid)) return;

    inFlight.current.add(cid);
    try {
      const res = await getCompetiteurById(cid);
      const obj = res?.data || res; // fallback
      const nom = obj?.nom_competiteur || obj?.nom || "";
      const prenom = obj?.prenom_competiteur || obj?.prenom || "";
      setById((prev) => ({
        ...prev,
        [cid]: { nom, prenom },
      }));
    } catch (e) {
      // on met quand même un placeholder pour éviter de refetch en boucle
      setById((prev) => ({
        ...prev,
        [cid]: { nom: "", prenom: "" },
      }));
    } finally {
      inFlight.current.delete(cid);
    }
  };

  /* =========================
     State édition structuré
  ========================= */
  const [edit, setEdit] = useState({
    issue: "V1", // V1 | V2 | N | H1 | H2 | F1 | F2
    decision: "Points", // texte libre affiché dans details_combat (si V1/V2)
    s1: { ippon: 0, wazari: 0, yuko: 0, shido: 0 },
    s2: { ippon: 0, wazari: 0, yuko: 0, shido: 0 },
  });

  /* =========================
     Fix “1 caractère” :
     resync localCombats uniquement
     quand on n'édite pas
  ========================= */
  useEffect(() => {
    if (!isOpen) return;
    if (editingId !== null) return;
    setLocalCombats(Array.isArray(combats) ? combats : []);
  }, [combats, isOpen, editingId]);

  useEffect(() => {
    if (!isOpen) {
      setEditingId(null);
      setSavingId(null);
      setDeletingId(null);
    }
  }, [isOpen]);

  /* =========================
     Préfetch noms compétiteurs
  ========================= */
  useEffect(() => {
    if (!isOpen) return;
    const safe = Array.isArray(combats) ? combats : [];
    const ids = new Set();
    safe.forEach((m) => {
      if (m?.competiteur_id1) ids.add(m.competiteur_id1);
      if (m?.competiteur_id2) ids.add(m.competiteur_id2);
    });
    ids.forEach((cid) => {
      if (!byId?.[cid]) fetchCompetiteur(cid);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, combats]);

  const rows = useMemo(() => {
    const safe = Array.isArray(localCombats) ? localCombats : [];
    return [...safe].sort(
      (a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0)
    );
  }, [localCombats]);

  if (!isOpen) return null;

  const Cell = ({ children, className = "" }) => (
    <td
      className={`px-3 py-2 align-top text-sm border-b border-black/10 dark:border-white/10 ${className}`}
    >
      {children}
    </td>
  );

  const guessIssueFromLetters = (r1, r2) => {
    // r1/r2 = V/D/N/H/F
    if (r1 === "N" && r2 === "N") return "N";
    if (r1 === "V" && r2 === "D") return "V1";
    if (r1 === "D" && r2 === "V") return "V2";
    if (r1 === "H" && r2 === "V") return "H1";
    if (r1 === "V" && r2 === "H") return "H2";
    if (r1 === "F" && r2 === "V") return "F1";
    if (r1 === "V" && r2 === "F") return "F2";
    // fallback
    return "V1";
  };

  const beginEdit = (m) => {
    const id = m?.id;
    if (!id) return;

    // assure cache noms
    fetchCompetiteur(m?.competiteur_id1);
    fetchCompetiteur(m?.competiteur_id2);

    const issue = guessIssueFromLetters(
      m?.resultat_competiteur1,
      m?.resultat_competiteur2
    );

    // si tu stockes déjà points_competiteur1/2, on peut s’en servir en affichage
    // mais les compteurs (ippon/wazari/yuko/shido) sont à saisir manuellement ici
    // => on initialise à 0 (ou tu peux parser details_combat si tu veux)
    setEdit({
      issue,
      decision: "Points",
      s1: { ippon: 0, wazari: 0, yuko: 0, shido: 0 },
      s2: { ippon: 0, wazari: 0, yuko: 0, shido: 0 },
    });

    setEditingId(id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEdit({
      issue: "V1",
      decision: "Points",
      s1: { ippon: 0, wazari: 0, yuko: 0, shido: 0 },
      s2: { ippon: 0, wazari: 0, yuko: 0, shido: 0 },
    });
    setLocalCombats(Array.isArray(combats) ? combats : []);
  };

  const lettersFromIssue = (issue) => {
    // retourne { r1, r2 } (V/D/N/H/F)
    switch (issue) {
      case "V1":
        return { r1: "V", r2: "D" };
      case "V2":
        return { r1: "D", r2: "V" };
      case "N":
        return { r1: "N", r2: "N" };
      case "H1": // comp1 hansoku
        return { r1: "H", r2: "V" };
      case "H2": // comp2 hansoku
        return { r1: "V", r2: "H" };
      case "F1": // comp1 forfait
        return { r1: "F", r2: "V" };
      case "F2": // comp2 forfait
        return { r1: "V", r2: "F" };
      default:
        return { r1: "V", r2: "D" };
    }
  };

  const saveEdit = async (m) => {
    const id = m?.id;
    if (!id) return;

    const cid1 = m?.competiteur_id1;
    const cid2 = m?.competiteur_id2;

    const name1 = getName(cid1);
    const name2 = getName(cid2);

    const details = buildDetailsCombat({
      issue: edit.issue,
      decision: edit.decision,
      name1,
      name2,
      s1: edit.s1,
      s2: edit.s2,
    });

    const p1 = computePoints(edit.s1);
    const p2 = computePoints(edit.s2);

    const { r1, r2 } = lettersFromIssue(edit.issue);

    const payload = {
      resultat_competiteur1: r1,
      resultat_competiteur2: r2,
      points_competiteur1: p1,
      points_competiteur2: p2,
      details_combat: details,
    };

    setSavingId(id);
    try {
      const res = await updateCombat(id, payload);
      if (res === null) throw new Error("updateCombat failed");

      setLocalCombats((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...payload } : c))
      );

      toast.success("Combat mis à jour ✅");
      setEditingId(null);
      onChanged?.();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour du combat.");
    } finally {
      setSavingId(null);
    }
  };

  const removeCombat = async (id) => {
    const typed = prompt(`Pour supprimer, tapez exactement : "SUPPRIMER ${id}"`);
    if (typed !== `SUPPRIMER ${id}`) {
      toast.error("Texte incorrect. Suppression annulée.");
      return;
    }

    const ok = window.confirm("Supprimer définitivement ce combat ?");
    if (!ok) return;

    setDeletingId(id);
    try {
      const res = await deleteCombat(id);
      if (res === null) throw new Error("deleteCombat failed");

      setLocalCombats((prev) => prev.filter((c) => c.id !== id));
      toast.success("Combat supprimé ✅");
      if (editingId === id) cancelEdit();
      onChanged?.();
    } catch (e) {
      toast.error("Erreur lors de la suppression du combat.");
    } finally {
      setDeletingId(null);
    }
  };

  const FighterBadge = ({ label, name, variant }) => {
    const cls =
      variant === "red"
        ? "bg-red-600 text-white"
        : "bg-white text-slate-900 border border-black/10 dark:bg-slate-100 dark:text-slate-900";
    return (
      <div className={`rounded-lg px-3 py-2 text-sm font-extrabold ${cls}`}>
        {label} : <span className="font-black">{name}</span>
      </div>
    );
  };

  const ScoreBox = ({ title, color, name, scores, onChange }) => {
    const pts = computePoints(scores);

    return (
      <div className="rounded-xl border border-black/10 dark:border-white/10 overflow-hidden">
        <div className={`px-3 py-2 text-sm font-extrabold ${color}`}>
          {title} — {name}
        </div>

        <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
          {[
            ["ippon", "Ippon"],
            ["wazari", "Waza-ari"],
            ["yuko", "Yuko"],
            ["shido", "Shido"],
          ].map(([k, label]) => (
            <div key={k}>
              <label className="text-xs text-slate-500 dark:text-slate-400">
                {label}
              </label>
              <input
                type="number"
                min={0}
                inputMode="numeric"
                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                value={String(scores[k] ?? 0)}
                onChange={(e) => onChange(k, e.target.value)}
              />
            </div>
          ))}
        </div>

        <div className="px-3 pb-3">
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-black/5 dark:border-white/10 px-3 py-2 text-sm">
            <span className="font-semibold">Total :</span> {pts} pts{" "}
            <span className="text-xs text-slate-500 dark:text-slate-400">
              (Shido = 0 pt)
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div className="relative z-10 w-full max-w-6xl bg-white dark:bg-slate-900 rounded-t-2xl sm:rounded-2xl border border-black/10 dark:border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-black/10 dark:border-white/10 bg-white/80 dark:bg-slate-900/80 backdrop-blur flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FaListUl className="text-slate-500 dark:text-slate-300" />
            <div>
              <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white">
                Détails des combats
              </h2>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300">
                {rows.length} combat{rows.length > 1 ? "s" : ""} listé
                {rows.length > 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white px-3 py-2 text-sm font-semibold"
          >
            <FaTimes />
            Fermer
          </button>
        </div>

        {/* Table */}
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full table-fixed text-left">
            <colgroup>
              <col className="w-20" />
              <col />
              <col className="w-44" />
            </colgroup>

            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border-b border-black/10 dark:border-white/10">
              <tr>
                <th className="px-3 py-2 text-xs sm:text-sm font-semibold">ID</th>
                <th className="px-3 py-2 text-xs sm:text-sm font-semibold">
                  Détails du combat
                </th>
                <th className="px-3 py-2 text-xs sm:text-sm font-semibold">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {rows.map((m) => {
                const id = m?.id ?? "—";
                const isEditing = editingId === id;
                const isSaving = savingId === id;
                const isDeleting = deletingId === id;

                const cid1 = m?.competiteur_id1;
                const cid2 = m?.competiteur_id2;
                const name1 = getName(cid1);
                const name2 = getName(cid2);

                const computedDetails = isEditing
                  ? buildDetailsCombat({
                      issue: edit.issue,
                      decision: edit.decision,
                      name1,
                      name2,
                      s1: edit.s1,
                      s2: edit.s2,
                    })
                  : null;

                return (
                  <tr
                    key={id}
                    className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40"
                  >
                    <Cell className="font-semibold text-slate-900 dark:text-white">
                      {id}
                    </Cell>

                    <Cell className="text-slate-700 dark:text-slate-200">
                      {/* Bandeaux noms */}
                      <div className="mb-3 grid sm:grid-cols-2 gap-2">
                        <FighterBadge
                          label="Compétiteur 1 (Rouge)"
                          name={name1}
                          variant="red"
                        />
                        <FighterBadge
                          label="Compétiteur 2 (Blanc)"
                          name={name2}
                          variant="white"
                        />
                      </div>

                      {isEditing ? (
                        <div className="space-y-3">
                          {/* 2 panels score */}
                          <div className="grid md:grid-cols-2 gap-3">
                            <ScoreBox
                              title="Rouge"
                              name={name1}
                              color="bg-red-600 text-white"
                              scores={edit.s1}
                              onChange={(k, v) =>
                                setEdit((s) => ({
                                  ...s,
                                  s1: { ...s.s1, [k]: safeInt(v) },
                                }))
                              }
                            />
                            <ScoreBox
                              title="Blanc"
                              name={name2}
                              color="bg-white text-slate-900 border-b border-black/10 dark:bg-slate-100"
                              scores={edit.s2}
                              onChange={(k, v) =>
                                setEdit((s) => ({
                                  ...s,
                                  s2: { ...s.s2, [k]: safeInt(v) },
                                }))
                              }
                            />
                          </div>

                          {/* Issue + décision */}
                          <div className="grid sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-slate-500 dark:text-slate-400">
                                Résultat (V/D/N/H/F)
                              </label>
                              <select
                                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                                value={edit.issue}
                                onChange={(e) =>
                                  setEdit((s) => ({ ...s, issue: e.target.value }))
                                }
                              >
                                <option value="V1">Rouge : V / Blanc : D</option>
                                <option value="V2">Rouge : D / Blanc : V</option>
                                <option value="N">Nul : N / N</option>
                                <option value="H1">Rouge : H / Blanc : V</option>
                                <option value="H2">Rouge : V / Blanc : H</option>
                                <option value="F1">Rouge : F / Blanc : V</option>
                                <option value="F2">Rouge : V / Blanc : F</option>
                              </select>
                            </div>

                            <div>
                              <label className="text-xs text-slate-500 dark:text-slate-400">
                                Décision (affichée)
                              </label>
                              <input
                                className="mt-1 w-full rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
                                value={edit.decision}
                                onChange={(e) =>
                                  setEdit((s) => ({ ...s, decision: e.target.value }))
                                }
                                placeholder="Ex: Points / Hantei / Ippon / …"
                                disabled={edit.issue === "H1" || edit.issue === "H2" || edit.issue === "F1" || edit.issue === "F2" || edit.issue === "N"}
                              />
                              {(edit.issue === "H1" ||
                                edit.issue === "H2" ||
                                edit.issue === "F1" ||
                                edit.issue === "F2" ||
                                edit.issue === "N") && (
                                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                  Auto : {decisionLabelFromIssue(edit.issue, edit.decision)}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* details_combat auto */}
                          <div>
                            <label className="text-xs text-slate-500 dark:text-slate-400">
                              details_combat (auto-généré)
                            </label>
                            <textarea
                              rows={3}
                              readOnly
                              className="mt-1 w-full rounded-lg border border-gray-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 px-3 py-2 text-sm"
                              value={computedDetails || ""}
                            />
                          </div>

                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => saveEdit(m)}
                              disabled={isSaving || isDeleting}
                              className="inline-flex items-center justify-center rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 text-sm font-semibold disabled:opacity-60"
                            >
                              {isSaving ? "Enregistrement..." : "Enregistrer"}
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={isSaving || isDeleting}
                              className="inline-flex items-center justify-center rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-white px-3 py-2 text-sm font-semibold disabled:opacity-60"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="whitespace-pre-wrap break-words">
                          {renderDetails(m?.details_combat || "—")}
                        </div>
                      )}
                    </Cell>

                    <Cell>
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => beginEdit(m)}
                          disabled={isDeleting || isSaving}
                          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-semibold text-slate-800 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/40 disabled:opacity-60"
                        >
                          <FaPen />
                          Modifier
                        </button>

                        <button
                          onClick={() => removeCombat(id)}
                          disabled={isDeleting || isSaving}
                          className="inline-flex items-center gap-2 rounded-lg border border-red-300/70 dark:border-red-800/50 bg-red-50/70 dark:bg-red-900/20 px-3 py-2 text-sm font-semibold text-red-700 dark:text-red-200 hover:bg-red-100/70 dark:hover:bg-red-900/30 disabled:opacity-60"
                        >
                          <FaTrash />
                          {isDeleting ? "Suppression..." : "Supprimer"}
                        </button>
                      </div>
                    </Cell>
                  </tr>
                );
              })}

              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400"
                  >
                    Aucun combat saisi pour l’instant.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pas de footer */}
      </div>
    </div>
  );
};

export default CombatDetailsModal;